/**
 * Parses an aspect ratio string (e.g., "16:9") into width and height numbers
 * @param aspectRatio - String in format "width:height"
 * @returns Object with width and height as numbers, or null if invalid
 */
export function parseAspectRatio(aspectRatio: string): { width: number; height: number } | null {
	if (!aspectRatio || typeof aspectRatio !== 'string') {
		return null;
	}

	const parts = aspectRatio.trim().split(':');
	if (parts.length !== 2) {
		return null;
	}

	const width = parseFloat(parts[0]);
	const height = parseFloat(parts[1]);

	if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
		return null;
	}

	return { width, height };
}

/**
 * Validates an aspect ratio string format
 * @param aspectRatio - String in format "width:height"
 * @returns true if valid, false otherwise
 */
export function isValidAspectRatio(aspectRatio: string): boolean {
	return parseAspectRatio(aspectRatio) !== null;
}

/**
 * Converts an aspect ratio string to a decimal float (width / height)
 * @param aspectRatio - String in format "width:height"
 * @returns Decimal ratio or null if invalid
 */
export function aspectRatioToFloat(aspectRatio: string): number | null {
	const parsed = parseAspectRatio(aspectRatio);
	if (!parsed) {
		return null;
	}
	return parsed.width / parsed.height;
}

/**
 * Converts a decimal ratio to an aspect ratio string
 * @param ratio - Decimal ratio (width / height)
 * @param precision - Number of decimal places to round to (default: 2)
 * @returns Aspect ratio string in format "width:height"
 */
export function floatToAspectRatio(ratio: number, precision: number = 2): string {
	if (isNaN(ratio) || ratio <= 0) {
		throw new Error('Ratio must be a positive number');
	}

	// Find the greatest common divisor to simplify the ratio
	const gcd = (a: number, b: number): number => {
		return b === 0 ? a : gcd(b, a % b);
	};

	// Convert to integers by multiplying by 10^precision
	const multiplier = Math.pow(10, precision);
	const width = Math.round(ratio * multiplier);
	const height = multiplier;

	const divisor = gcd(width, height);

	return `${width / divisor}:${height / divisor}`;
}

/**
 * Calculates dimensions for a given aspect ratio and target dimension
 * @param aspectRatio - String in format "width:height"
 * @param targetWidth - Target width (if provided, height will be calculated)
 * @param targetHeight - Target height (if provided, width will be calculated)
 * @returns Object with calculated width and height, or null if invalid
 */
export function calculateDimensions(
	aspectRatio: string,
	targetWidth?: number,
	targetHeight?: number
): { width: number; height: number } | null {
	const ratio = aspectRatioToFloat(aspectRatio);
	if (!ratio) {
		return null;
	}

	if (targetWidth && targetWidth > 0) {
		return {
			width: targetWidth,
			height: Math.round(targetWidth / ratio)
		};
	}

	if (targetHeight && targetHeight > 0) {
		return {
			width: Math.round(targetHeight * ratio),
			height: targetHeight
		};
	}

	return null;
}

/**
 * `W:H` reduced by the gcd, or null unless both values round to finite integers above 0.
 * Dependency-free on purpose: the browser renderer bundle imports it too.
 */
export function reduceAspectRatio(width: unknown, height: unknown): string | null {
	let a = Math.round(Number(width));
	let b = Math.round(Number(height));
	if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) {
		return null;
	}

	const w = a;
	const h = b;
	while (b !== 0) {
		[a, b] = [b, a % b];
	}

	return `${w / a}:${h / a}`;
}
